import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, BadgeCheck } from "lucide-react";
import { useApp, useUI } from "@/context/AppContext";
import { resolveMedia } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { MaskLine, Reveal } from "@/components/Reveal";
import { Highlight } from "@/components/Highlight";
import { SectionHeading, StatCounter } from "@/components/primitives";
import Marquee from "@/components/Marquee";

export default function Home() {
  const { t, m, lang } = useApp();
  const ui = useUI();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useSEO(
    { fr: "Agrivoire Export SARL — Courtage & conseil agricole international", en: "Agrivoire Export SARL — International agricultural brokerage & consulting" },
    { fr: "Agrivoire Export SARL connecte les matières premières agricoles ivoiriennes aux marchés internationaux : cacao, cajou, fruits exotiques, caoutchouc, kola.", en: "Agrivoire Export SARL connects Ivorian agricultural commodities to international markets: cocoa, cashew, exotic fruits, rubber, kola." }
  );

  const stats = [
    { v: t("home", "stat_1_value"), l: t("home", "stat_1_label") },
    { v: t("home", "stat_2_value"), l: t("home", "stat_2_label") },
    { v: t("home", "stat_3_value"), l: t("home", "stat_3_label") },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] overflow-hidden bg-[#0A0A0A] text-white">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={resolveMedia(m("home_hero"))} alt="Navire cargo à quai — export international" className="h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0A] via-[#0A0A0A]/80 to-[#4E7D2C]/20" />
        </motion.div>

        <div className="relative mx-auto flex min-h-[92vh] max-w-[1400px] flex-col justify-center px-5 pt-24 pb-16 md:px-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <span className="inline-flex items-center gap-2 font-poppins text-xs font-bold uppercase tracking-[0.22em] text-[#8CC63F]">
              <span className="h-px w-10 bg-[#F5821F]" />
              {t("home", "hero_kicker")}
            </span>
          </motion.div>

          <h1 className="mt-6 max-w-5xl font-montserrat text-[10vw] font-black leading-[0.98] tracking-tight sm:text-5xl lg:text-[3.9rem] xl:text-[4.4rem]">
            <MaskLine delay={0.15}>{t("home", "hero_line1")}</MaskLine>
            <MaskLine delay={0.28} className="text-[#8CC63F]">{t("home", "hero_line2")}</MaskLine>
            <MaskLine delay={0.41}>{t("home", "hero_line3")}</MaskLine>
            <MaskLine delay={0.54}>
              <Highlight text={t("home", "hero_line4")} word={lang === "fr" ? "exigence" : "excellence"} className="text-[#F5821F]" />
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-8 max-w-2xl text-justify font-poppins text-base leading-relaxed text-white/75 md:text-lg"
          >
            {t("home", "hero_sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link to="/contact#buyer-form" data-testid="hero-cta-offer" className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-8 py-4 font-poppins text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:scale-105">
              {t("global", "cta_offer")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/contact#consulting-form" data-testid="hero-cta-appointment" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-poppins text-base font-semibold text-white transition-colors duration-300 hover:border-[#1CADE4] hover:text-[#1CADE4]">
              {t("global", "cta_appointment")}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* EXPERTISES */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32" data-testid="expertises-section">
        <SectionHeading kicker={t("home", "expertise_kicker")} title={t("home", "expertise_title")} />
        <div className="mt-14 grid gap-6 md:grid-cols-12">
          {[
            { to: "/courtage", n: "01", img: m("expertise_courtage"), title: t("home", "expertise_1_title"), desc: t("home", "expertise_1_desc"), span: "md:col-span-7" },
            { to: "/conseil", n: "02", img: m("expertise_conseil"), title: t("home", "expertise_2_title"), desc: t("home", "expertise_2_desc"), span: "md:col-span-5" },
          ].map((c, i) => (
            <Reveal key={c.n} delay={i * 0.1} className={c.span}>
              <Link to={c.to} data-testid={`expertise-card-${i}`} className="group relative flex h-full min-h-[420px] flex-col justify-end overflow-hidden rounded-3xl bg-neutral-900 p-8 text-white">
                <img src={resolveMedia(c.img)} alt={c.title} className="absolute inset-0 h-full w-full object-cover opacity-55 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                <div className="relative">
                  <span className="font-montserrat text-5xl font-black text-[#1CADE4]/80">{c.n}</span>
                  <h3 className="mt-4 max-w-md font-montserrat text-2xl font-bold leading-tight">{c.title}</h3>
                  <p className="mt-3 max-w-md font-poppins text-sm text-white/70">{c.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-poppins text-sm font-semibold text-[#8CC63F]">
                    {ui.read_more} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white py-24 md:py-32" data-testid="stats-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading kicker={t("home", "stats_kicker")} title={t("home", "stats_title")} />
              <Reveal delay={0.15}>
                <img src={resolveMedia(m("home_map"))} alt="Route d'export de la Côte d'Ivoire vers l'Europe" className="mt-10 w-full rounded-2xl border border-black/5" />
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <div className="grid gap-6 sm:grid-cols-1">
                {stats.map((s, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="flex items-center gap-6 border-b border-black/10 pb-6" data-testid={`stat-block-${i}`}>
                      <div className="font-montserrat text-5xl font-black tracking-tight text-transparent sm:text-6xl" style={{ backgroundImage: "linear-gradient(90deg,#8CC63F,#4E7D2C)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>
                        <StatCounter value={s.v} />
                      </div>
                      <p className="font-poppins text-base leading-snug text-neutral-700">{s.l}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-gradient-to-r from-[#4E7D2C] to-[#8CC63F] py-8" data-testid="marquee-section">
        <Marquee text={t("home", "marquee")} />
      </section>

      {/* GARANTIES */}
      <section className="relative overflow-hidden bg-[#0A0A0A] py-24 text-white md:py-32" data-testid="garanties-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionHeading light kicker={t("home", "garanties_kicker")} title={t("home", "garanties_title")} />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                <ShieldCheck className="h-9 w-9 text-[#FFD700]" />
                <p className="mt-5 font-poppins text-base leading-relaxed text-white/80">{t("home", "garanties_igei")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                <BadgeCheck className="h-9 w-9 text-[#8CC63F]" />
                <p className="mt-5 font-poppins text-base leading-relaxed text-white/80">{t("home", "garanties_producers")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] py-20" data-testid="cta-band">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-8 px-5 md:flex-row md:items-center md:px-10">
          <div>
            <h2 className="max-w-2xl font-montserrat text-3xl font-bold leading-tight text-white sm:text-4xl">{t("home", "cta_band_title")}</h2>
            <p className="mt-3 font-poppins text-white/85">{t("home", "cta_band_sub")}</p>
          </div>
          <Link to="/contact" data-testid="cta-band-button" className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#0A0A0A] px-8 py-4 font-poppins text-base font-semibold text-white transition-transform duration-300 hover:scale-105">
            {t("global", "cta_offer")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
